import React, { useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Calculator, Loader2, ShieldAlert } from "lucide-react";
import { useApp } from "@/context/AppContext";

export default function PositionSizer() {
  const { fmtCurrency } = useApp();
  const [inputs, setInputs] = useState({
    equity: 10000,
    entry: 1.10500,
    stop_loss: 1.10300,
    atr: 0.0015,
    risk_pct: 1.0,
    win_rate: 0.55,
    avg_win_loss_ratio: 1.5,
    method: "fixed_fractional",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calc = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/risk/position-size`, inputs);
      setResult(r.data);
    } finally { setLoading(false); }
  };

  const update = (k, v) => setInputs({ ...inputs, [k]: v });

  return (
    <div className="p-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="font-heading font-bold text-2xl tracking-tight" data-testid="ps-title">Calcul de taille de position</h1>
        <p className="text-white/50 text-sm mt-1">3 méthodes professionnelles. Comparez avant chaque trade réel.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="panel p-5 space-y-3">
          <div className="panel-title text-[14px] mb-2">Paramètres</div>
          <Field label="Capital ($)" k="equity" v={inputs.equity} onChange={update} type="number" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Prix d'entrée" k="entry" v={inputs.entry} onChange={update} type="number" step="0.00001" />
            <Field label="Stop-Loss" k="stop_loss" v={inputs.stop_loss} onChange={update} type="number" step="0.00001" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="ATR" k="atr" v={inputs.atr} onChange={update} type="number" step="0.0001" />
            <Field label="Risque / trade %" k="risk_pct" v={inputs.risk_pct} onChange={update} type="number" step="0.1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Winrate historique (0-1)" k="win_rate" v={inputs.win_rate} onChange={update} type="number" step="0.01" />
            <Field label="Ratio gains / pertes" k="avg_win_loss_ratio" v={inputs.avg_win_loss_ratio} onChange={update} type="number" step="0.1" />
          </div>
          <button className="btn btn-primary w-full justify-center mt-3" onClick={calc} disabled={loading} data-testid="ps-calc-btn">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Calculator size={14} />} Calculer
          </button>
        </div>

        <div className="panel p-5 space-y-3">
          <div className="panel-title text-[14px]">Recommandations</div>
          {!result && <div className="text-white/40 text-sm py-6 text-center">Renseignez vos paramètres et cliquez « Calculer ».</div>}
          {result && result.ok && (
            <>
              <div className="flex items-center justify-between px-3 py-2 rounded bg-hover border border-white/5">
                <span className="text-xs text-white/60">Montant risqué</span>
                <span className="font-mono text-sm text-warn">{fmtCurrency(result.risk_dollar)}</span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 rounded bg-hover border border-white/5">
                <span className="text-xs text-white/60">Distance stop</span>
                <span className="font-mono text-sm">{result.stop_distance}</span>
              </div>
              {Object.entries(result.sizes).map(([method, data]) => (
                <div key={method} className={`p-3 rounded border ${method === "fixed_fractional" ? "border-accent-blue/40 bg-accent-blue/5" : "border-white/5 bg-hover"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-wide text-white/60">{methodLabel(method)}</span>
                    <span className="font-mono text-sm text-white">Taille : {data.size}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">
                    <div className="bg-white/[0.03] rounded px-2 py-1">
                      <span className="text-white/40">Notionnel : </span>
                      <span className="font-mono text-white/85">{fmtCurrency(data.notional)}</span>
                    </div>
                    <div className="bg-white/[0.03] rounded px-2 py-1">
                      <span className="text-white/40">Risque : </span>
                      <span className="font-mono text-warn">{fmtCurrency(data.dollar_risk)}</span>
                    </div>
                  </div>
                  <div className="text-[11px] text-white/50 mt-2">{data.explanation}</div>
                  {data.kelly_fraction_pct !== undefined && (
                    <div className="text-[11px] text-white/50 mt-1">Fraction Kelly appliquée : {data.kelly_fraction_pct}%</div>
                  )}
                </div>
              ))}
              <div className="flex items-start gap-2 text-[11px] text-white/50 mt-2">
                <ShieldAlert size={12} className="text-warn shrink-0 mt-0.5" />
                <div>Rappel : ne risquez jamais plus de 1-2% par trade et 6% du capital simultanément. Ces limites sont statistiquement démontrées comme optimales pour la survie long-terme.</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function methodLabel(k) {
  return {
    fixed_fractional: "Fixed Fractional (recommandé)",
    volatility_adjusted: "Ajusté à la volatilité (ATR)",
    kelly_quarter: "Kelly / 4 (sécurisé)",
  }[k] || k;
}

function Field({ label, k, v, onChange, ...rest }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="field" value={v} onChange={(e) => onChange(k, rest.type === "number" ? +e.target.value : e.target.value)} {...rest} data-testid={`ps-${k}`} />
    </div>
  );
}
